<script setup lang="ts">
/**
 * One agency card on the public demo directory (/demos). Links straight to
 * the tenant's live site at {slug}.{platformRootDomain} via useCommunityLinks,
 * so the same card works in local dev (*.localhost:3000) and prod
 * (*.zerotohero.network) without env mismatches.
 *
 * Cover falls back to a gradient built from the tenant's primary colour when
 * no cover image is set, so freshly-seeded demos still look intentional.
 */
import type { PublicTenantCard } from '~/repositories/tenantRepository'

const props = defineProps<{ agency: PublicTenantCard }>()

const { getCommunityHref } = useCommunityLinks()

const href = computed(() =>
  getCommunityHref({ slug: props.agency.slug, domain: props.agency.domain })
)

/**
 * Use the tenant's own brand colour when set; otherwise fall through to the
 * palette gradient class on the element. Runtime data, so it never trips the
 * source-level color guard (no hex literals in this file).
 */
const accentStyle = computed(() =>
  props.agency.primary_color ? { background: props.agency.primary_color } : undefined
)

/** Human label for the tenant category slug (event_planning -> Wedding & Events). */
const categoryLabel = computed(() => {
  const labels: Record<string, string> = {
    migration: 'Migration',
    education: 'Education',
    legal: 'Law firm',
    law: 'Law firm',
    real_estate: 'Real estate'
  }
  const c = props.agency.category
  if (!c) return null
  return labels[c] ?? c.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())
})

const initials = computed(() =>
  props.agency.name
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('')
)
</script>

<template>
  <a
    :href="href"
    target="_blank"
    rel="noopener"
    class="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
  >
    <!-- Cover -->
    <div class="relative h-32 w-full">
      <img
        v-if="agency.cover_url"
        :src="agency.cover_url"
        :alt="agency.name"
        class="h-full w-full object-cover"
      >
      <div
        v-else
        class="h-full w-full bg-gradient-to-br from-indigo-500 to-purple-500"
        :style="accentStyle"
      />
      <!-- Logo / initials badge -->
      <div class="absolute -bottom-6 left-5">
        <img
          v-if="agency.logo_url"
          :src="agency.logo_url"
          :alt="agency.name"
          class="h-12 w-12 rounded-xl border-2 border-white bg-white object-cover shadow-sm"
        >
        <div
          v-else
          class="flex h-12 w-12 items-center justify-center rounded-xl border-2 border-white bg-gradient-to-br from-indigo-500 to-purple-500 text-sm font-semibold text-white shadow-sm"
          :style="accentStyle"
        >
          {{ initials }}
        </div>
      </div>
    </div>

    <!-- Body -->
    <div class="flex flex-1 flex-col px-5 pt-8 pb-5">
      <div class="flex items-start justify-between gap-2">
        <h3 class="font-semibold text-gray-900">{{ agency.name }}</h3>
        <span
          v-if="categoryLabel"
          class="shrink-0 rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-600"
        >{{ categoryLabel }}</span>
      </div>

      <p
        v-if="agency.description"
        class="mt-2 line-clamp-3 text-sm leading-relaxed text-gray-600"
      >{{ agency.description }}</p>

      <span
        class="mt-4 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 group-hover:gap-1.5"
      >
        View live demo
        <span
          aria-hidden="true"
          class="transition-transform group-hover:translate-x-0.5"
        >↗</span>
      </span>
    </div>
  </a>
</template>
